import { bootstrapSession } from '@/shared/api/apolloClient';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/graphql';
const REST_BASE = API_URL.replace(/\/graphql\/?$/, '');

/** Max size accepted by the API (`USER_PHOTO_MAX_BYTES`); checked here for a friendlier error. */
export const USER_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export const USER_PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

/** `photo_url` from GraphQL is a relative API path; turn it into something an <img> can load. */
export function userPhotoSrc(photo_url: string | null | undefined): string | null {
  return photo_url ? `${REST_BASE}${photo_url}` : null;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().access_token;
  const ws = useWorkstationStore.getState().workstation_id;
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (ws) h['X-Workstation-Id'] = ws;
  return h;
}

async function authedFetch(url: string, init: RequestInit): Promise<Response> {
  const attempt = (): Promise<Response> =>
    fetch(url, { ...init, credentials: 'include', headers: authHeaders() });
  let res = await attempt();
  if (res.status === 401 && (await bootstrapSession())) res = await attempt();
  return res;
}

async function errorCode(res: Response, fallback: string): Promise<Error> {
  if (res.status === 413) return new Error('user_photo_too_large');
  try {
    const j = (await res.json()) as { message?: string };
    if (typeof j.message === 'string') return new Error(j.message);
  } catch {
    /* non-JSON error body */
  }
  return new Error(fallback);
}

export async function uploadUserPhoto(user_id: string, file: File): Promise<void> {
  if (file.size > USER_PHOTO_MAX_BYTES) throw new Error('user_photo_too_large');
  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await authedFetch(`${REST_BASE}/users/${user_id}/photo`, { method: 'POST', body: fd });
  if (!res.ok) throw await errorCode(res, 'user_photo_upload_failed');
}

export async function deleteUserPhoto(user_id: string): Promise<void> {
  const res = await authedFetch(`${REST_BASE}/users/${user_id}/photo`, { method: 'DELETE' });
  if (!res.ok) throw await errorCode(res, 'user_photo_delete_failed');
}
